/**
 *  Prototype enhancing default browser console handler
 *
 *  Quick decoration: Adding Sentry exception hit on `console.error`
 *
 *  @param {object} handler default is the global `console`
 *  @param {string} callee "type:name" optional, types are "plugin",
 *                  "assets", "middleware" and "component",
 */
import { typeOf, isEmpty, head, split, drop, join, when, pipe_ } from '~/utils/lib/core/prelude'
import pubSub from '~/utils/lib/core/pub-sub'
import { EVENTS as CORE_EVENTS } from '~/utils/lib/core/types'

/**
 * @constructor
 * @param {string} callee eg "plugin:core"
 */
export default function Console(callee) {
  this.callee = callee
  this.handler = console // unpure
}

/// /!\ Avoid programatically prototype enhancing method,
///     as JSDoc Intellisence won't understand these additions
///
///     Prefer repetition here
// const TYPES = ['log', 'info', 'error', 'warn', 'debug']

// TYPES.forEach(type => {
//   Console.prototype[type] = function() {
//     return this.write(type)(...arguments)
//   }
// })

Console.prototype.log = function() {
  return this.write('log')(...arguments)
}
Console.prototype.info = function() {
  return this.write('info')(...arguments)
}
Console.prototype.error = function() {
  pubSub.publish(
    CORE_EVENTS.ISSUE_MONITORED,
    { level: 'error', logs: [ ...arguments ] }
  )
  return this.write('error')(...arguments)
}
Console.prototype.warn = function() {
  pubSub.publish(
    CORE_EVENTS.ISSUE_MONITORED,
    { level: 'warn', logs: [ ...arguments ] }
  )
  return this.write('warn')(...arguments)
}
Console.prototype.debug = function() {
  return this.write('debug')(...arguments)
}

Console.prototype.write = function(/** @type {string} */ type) {
  return (/** @type {any[]} */...messages) => {
    try {
      // @ts-ignore: JSDoc ES5 Creational pattern limitation
      const row = new Row()
      const calleeType = pipe_(
        this.callee,
        split(':'),
        head,
        when(
          isEmpty,
          () => ''
        )
      )
      const calleeName = pipe_(
        this.callee,
        split(':'),
        drop(1),
        when(
          isEmpty,
          () => ''
        ),
        join(':')
      )
      // Add the bullet
      row.push(getBulletContent(type), getBulletStyle(type))
      // Add the date
      row.push(getCreationDate(), 'font-size: 9px; padding: 4px 5px;')
      // Add the callee
      row.push(calleeName, getCalleeStyle(calleeType))
      // Add provided messages
      messages.forEach(row.push)
      // Output
      this.handler.log.apply(console, row.toArray())
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(messages)
    }
  }
}
/**
 *  Get Bullet Content
 *  @private
 *  @param {string} type
 *  @return {string}
 */
function getBulletContent(type) {
  return type === 'log' ? '▷' : '▶'
}
/**
 *  Get Bullet Style
 *  @param {string} type
 *  @return {string}
 */
function getBulletStyle(type) {
  let style = 'color: '

  switch (type) {
    case 'error':
      style += '#BF3F3F'
      break
    case 'warn':
      style += '#D4CC5B'
      break
    case 'info':
      style += '#69A1F0'
      break
    default:
      style += '#A9A9A9'
      break
  }

  return style + '; padding: 4px 5px; font-weight: bold;'
}
/**
 *  Get Creation Date
 *  @return {string}
 */
function getCreationDate() {
  return new Date().toLocaleTimeString()
}
/**
 *  Get Callee Style
 *  @param {string} type
 *  @return {string}
 */
function getCalleeStyle(type) {
  let style = 'color: #FFFFFF; background: '

  switch (type) {
    case 'plugin':
      style += '#A1D490'
      break
    case 'component':
      style += '#D4A190'
      break
    case 'middleware':
      style += '#B6CDB9'
      break
    case 'store':
      style += '#90C3D4'
      break
  }

  return style + '; padding: 4px 5px; font-size: 10px;'
}
/** @constructs Row */
const Row = function() {
  // A list of cells contained in the row
  const /** @type {{ style?: string ; content: any }[]} */ cells = []

  return {
    /**
     *  Push
     *        - add a cell into the row
     *  @public
     *  @param {any} content
     *  @param {string} style [optional]
     */
    push: function(content, style) {
      const /** @type {{ style?: string ; content: any }} */ cell = {
        content: content
      }
      // Inject style if provided
      if (typeof(style) === 'string') cell.style = style
      // Add the cell
      cells.push(cell)
    },
    /**
     *  To Array
     *  @public
     *  @return {string[]}
     */
    toArray: function() {
      let stylizedContents = ''
      const styleRules = []
      const miscContents = []

      for (let idx = 0; idx < cells.length; idx++) {
        // @ts-ignore: UNSAFE coerce
        const /** @type {{ style: string ; content: any }} */ cell = cells[idx]
        // Cell without style case
        if (!('style' in cell)) {
          miscContents.push(/** @type {{ style: string ; content: any }} */ (cell).content)
          continue
        }
        // Stylized cell
        stylizedContents += '%c' + cell.content
        styleRules.push(cell.style)
      }

      return isEmpty(stylizedContents)
        ? miscContents
        : [stylizedContents].concat(styleRules, miscContents)
    }
  }
}
